import { useState, type ReactNode } from 'react'
import { QrCode } from '@phosphor-icons/react'
import { useI18n, type MessageKey } from '@/i18n'
import { Button, ScannerSheet, type ButtonProps } from '@/ui'
import type { RefusalReason, RefusedScan, TagEntry } from '../commands'
import { checkCode, expandTypedCode, type GuardContext } from '../model/scanGuard'

const REFUSAL_KEYS: Record<RefusalReason, MessageKey> = {
  TAG_UNKNOWN: 'gate.refuse.unknown',
  TAG_WRONG_KIND: 'gate.refuse.wrongKind',
  ROLL_NOT_CONFIRMED: 'gate.refuse.notConfirmed',
  TAG_VOID: 'gate.refuse.void',
  TAG_ALREADY_USED: 'gate.refuse.used',
  TAG_DUPLICATE_SCAN: 'gate.refuse.duplicate',
}

function useRefusalText() {
  const { t } = useI18n()
  return (r: RefusalReason) => t(REFUSAL_KEYS[r])
}

/**
 * A big Scan button that opens the camera (with 'Type number' fallback) and runs the local tag
 * guard on every code. Refused codes are reported to `onRefuse` (the refused-scan log, F4.6-c)
 * and the phone beeps and stays on the scanner.
 */
export function GuardedScanButton({
  guard,
  onAccept,
  onRefuse,
  label,
  sheetTitle,
  extraCheck,
  ...button
}: {
  guard: GuardContext
  onAccept: (payload: string, entry: TagEntry) => void
  onRefuse?: (scan: RefusedScan) => void
  label: ReactNode
  sheetTitle?: ReactNode
  /** Screen-specific refusal (e.g. "this tag is not on a bag of this delivery"); return a message. */
  extraCheck?: (payload: string) => string | null
} & Omit<ButtonProps, 'onClick' | 'children'>) {
  const refusal = useRefusalText()
  const [open, setOpen] = useState(false)
  const confirmed = guard.rolls.filter((r) => guard.confirmedBatchIds.has(r.batch_id))
  return (
    <>
      <Button
        size="xl"
        fullWidth
        leadingIcon={<QrCode aria-hidden="true" />}
        {...button}
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <ScannerSheet
        open={open}
        onClose={() => setOpen(false)}
        title={sheetTitle ?? label}
        normalize={(c) => expandTypedCode(c, guard.kind, confirmed)}
        validate={(code) => {
          const extra = extraCheck?.(code)
          if (extra) return extra
          const res = checkCode(code, guard)
          if (res.ok) return null
          onRefuse?.({
            payload: res.payload,
            reason: res.reason,
            occurred_at: new Date().toISOString(),
          })
          return refusal(res.reason)
        }}
        onScan={(code, meta) => onAccept(code, meta.entry)}
      />
    </>
  )
}

/** A Scan button without the tag guard (cancel scans, dispute bag pick). */
export function PlainScanButton({
  onScan,
  validate,
  label,
  normalize,
  ...button
}: {
  onScan: (payload: string, entry: TagEntry) => void
  validate?: (code: string) => string | null
  normalize?: (code: string) => string
  label: ReactNode
} & Omit<ButtonProps, 'onClick' | 'children'>) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button
        size="xl"
        fullWidth
        leadingIcon={<QrCode aria-hidden="true" />}
        {...button}
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <ScannerSheet
        open={open}
        onClose={() => setOpen(false)}
        title={label}
        validate={validate}
        normalize={normalize}
        onScan={(code, meta) => onScan(code, meta.entry)}
      />
    </>
  )
}
